import {ReactNode, useContext, useEffect, useState, useRef} from 'react';
import {v4 as uuid} from 'uuid';
import Select from 'react-select';
import sortBy from 'lodash/sortBy';
import {
  Button,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import {format} from 'd3';
import {useNavigate} from 'react-router';
import moment from 'moment';

import {REPORTS_ITEM_PATH, REPORTS_RELATIONSHIPS_PATH, ADMINISTRATION_PATH} from 'src/routes';
import {GroupNode} from 'src/types';
import {WorkspaceContext} from 'src/context';
import {
  Layout,
  LoadingPlaceholder,
  MaterializedViewAlert,
  ImpactScore,
  WorkingOnIt,
  SizeContent,
} from 'src/components';
import {useQuery} from 'src/hooks/router';
import {DATE_FORMAT} from 'src/constants';

import ImpactBubbleHeaderView from './impactBubbleHeaderView';
import {ImpactScoreExplanationModal} from './ImpactScoreExplanationModal';
import ImpactLineGraphView from './impactLineGraphView';
import {ImpactBubbleChartView} from './ImpactBubbleChartView';
import {ImpactChartNoDataPlaceholder} from './components';
import {useApplicationStats, useGroups} from './hooks';
import {getMostRecentAgentType, formatDashboardNumbers} from './utils';


import './styles.css';
const f = format(',');

type ApplicationNode = GroupNode & {
  type: 'Application'
}

const isApplicationNode = (g: GroupNode): g is ApplicationNode => g.type === 'Application';

export const Dashboard = () => {
  const {currentWorkspace} = useContext(WorkspaceContext);
  const definitionId = currentWorkspace?.materializedViewDefinition?.id;
  const navigate = useNavigate();
  const {get} = useQuery();
  const applicationQueryParam = get('application');
  const {groups, loadGroups, loading, error} = useGroups(definitionId);
  const applications = groups?.filter(isApplicationNode);

  const setSelectedApplication = (name: string) => {
    navigate({
      search: `?application=${name}`,
    });
  };

  const matchedApplication = applications?.find(({name}) => name === applicationQueryParam) ?? null;
  useEffect(() => {
    if (!matchedApplication && applications?.length) {
      navigate(`${window.location.pathname}?application=${applications[0].name}`, {replace: true});
    }
  }, [matchedApplication, applications]);

  const application = matchedApplication;

  const {stats: applicationStats} = useApplicationStats(application);
  const contentRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState('Class');
  const [impactScore, setImpactScore] = useState<number>();
  const [isImpactDialogOpen, setIsImpactDialogOpen] = useState(false);
  const [, setHeaderWidth] = useState(0);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (application) {
      setImpactScore(determineImpactScore(application));
    }
  }, [application]);

  const determineImpactScore = (application: ApplicationNode) => {
    const score = (application?.properties['codelogic.quality.impactScore'] as number) || undefined;
    if (typeof score === 'number') {
      return Math.max(0, Math.min(1, score));
    } else {
      return score;
    }
  };

  const areStatsResolved = !!applicationStats;

  const handleResize = () => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  useEffect(() => {
    if (contentRef.current && dropRef.current && dropRef.current.offsetWidth > 0) {
      const headW = contentRef.current?.offsetWidth - dropRef.current?.offsetWidth;
      const dropW = dropRef.current.offsetWidth;
      setHeaderWidth(headW > dropW ? headW : dropW);
    }
  }, [contentRef.current, dropRef.current, dimensions]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize, false);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const sortedGroups = sortBy(groups, ({name}) => name.toLowerCase());

  return (
    <Layout>
      <MaterializedViewAlert dataAvailable={loadGroups}/>
      {!loading &&
        (!groups || groups.length === 0) &&
        !currentWorkspace?.materializedViewDefinition.primary &&
        <WorkingOnIt gotData={() => loadGroups(currentWorkspace?.materializedViewDefinition.id)}/>
      }
      {(currentWorkspace?.materializedViewDefinition.primary || (!currentWorkspace?.materializedViewDefinition.primary && groups && groups.length > 0)) && <Container fluid>
        <Row>
          <Col>
            <div className="dashboard-cont" ref={contentRef}>
              <div className="dash-header">
                <div className="dropdown-cont" ref={dropRef}>
                  <h1 style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 'var(--page-header-font-size)',
                    color: 'var(--color-extracted-4)',
                    minHeight: '3.375rem',
                    flex: 1,
                  }}>
                    <span style={{paddingRight: '1rem'}}>Dashboard</span>{' '}
                    {groups && !!groups.length ? (
                      <div
                        style={{
                          display: 'inline-block',
                          width: '100%',
                        }}
                        title="Select Application"
                      >
                        <Select<GroupNode>
                          options={sortedGroups}
                          value={application}
                          getOptionLabel={({name}) => name}
                          getOptionValue={({name}) => name}
                          onChange={(g) => {
                            if (g?.name) {
                              setSelectedApplication(g.name);
                            }
                          }}
                        />
                      </div>
                    ) : (
                        <span
                          style={{
                            fontSize: '1rem',
                            padding: 0,
                            color: 'black',
                          }}
                        >(Install and configure an{' '}
                          <Button
                            style={{
                              padding: 0,
                            }}
                            color="link"
                            onClick={() =>
                              navigate(`${ADMINISTRATION_PATH}?tab=agents`)
                            }
                          >agent</Button>{' '}
                          to run your first scan.)
                        </span>
                    )}
                  </h1>
                  {application && definitionId && (
                    <div style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
                      {typeof impactScore === 'number' && (
                        <button
                          className="Dashboard__ImpactScore__button"
                          onClick={() => setIsImpactDialogOpen(true)}
                        >
                          <div style={{
                            margin: '0rem',
                            backgroundColor: 'white',
                            border: '1px solid rgb(204, 204, 204)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                          }}>

                            <span style={{fontSize: '0.75rem', color: 'gray', textTransform: 'uppercase'}}>Impact Score</span>
                            <ImpactScore
                              size={90}
                              score={impactScore}
                            />
                          </div>
                        </button>
                      )}
                      <div style={{
                        margin: '0 0 0 0.5rem',
                        position: 'relative',
                        display: 'flex',
                        width: '100%',
                        backgroundColor: 'white',
                        border: '1px solid rgb(204, 204, 204)',
                      }}>
                        <SizeContent>
                          {(size) => (
                            <ImpactLineGraphView
                              id={application?.id}
                              size={size}
                              setLoading={() => false}
                              definitionId={definitionId}
                            />
                          )}
                        </SizeContent>
                      </div>
                    </div>
                  )}
                </div>
                {application && groups && !!groups.length && (
                  <div className="StatsContainer">
                    <StatsRow
                      label="Classes"
                      onClick={() => navigate(`${REPORTS_ITEM_PATH}?applicationId=${encodeURIComponent(application.id)}&typeName=ClassEntity`)}
                      value={applicationStats?.nodeTypeCounts.ClassEntity}
                    />
                    <StatsRow
                      label="Methods"
                      value={applicationStats?.nodeTypeCounts.MethodEntity}
                      onClick={() => navigate(`${REPORTS_ITEM_PATH}?applicationId=${encodeURIComponent(application.id)}&typeName=MethodEntity`)}
                    />
                    <StatsRow
                      label="Databases"
                      value={applicationStats?.nodeTypeCounts.Database}
                      onClick={() => navigate(`${REPORTS_ITEM_PATH}?applicationId=${encodeURIComponent(application.id)}&typeName=Database`)}
                    />
                    <StatsRow
                      label="Column"
                      value={applicationStats?.nodeTypeCounts.Column}
                      onClick={() => navigate(`${REPORTS_ITEM_PATH}?applicationId=${encodeURIComponent(application.id)}&typeName=Column`)}
                    />
                    <StatsRow
                      label="Tables"
                      value={applicationStats?.nodeTypeCounts.Table || '0'}
                      onClick={() => navigate(`${REPORTS_ITEM_PATH}?applicationId=${encodeURIComponent(application.id)}&typeName=Tables`)}
                    />
                    <StatsRow
                      label="Foreign Keys"
                      value={applicationStats?.relationshipTypeCounts.FOREIGN_KEY_REFERENCE}
                      onClick={() => navigate(`${REPORTS_RELATIONSHIPS_PATH}?applicationLabel=${encodeURIComponent(application.name)}&relationshipType=FOREIGN_KEY_REFERENCE`)}
                    />
                    <StatsRow
                      label="Agents"
                      value={f(Object.keys(applicationStats?.agentTypeDates || []).length)}
                      onClick={() => navigate(`${ADMINISTRATION_PATH}?tab=agents`)}
                      hover={'Active Agents'}
                    />
                    <StatsRow
                      label="Latest Scan"
                      value={
                        <div style={{fontSize: '0.8625rem'}}>
                          <div>
                            {applicationStats?.lastAgentDate ?
                              moment(applicationStats.lastAgentDate).utc().format(DATE_FORMAT) :
                              'Never'}
                          </div>
                          <div>
                            <span>{areStatsResolved ? getMostRecentAgentType(applicationStats) : 'None'}</span>
                          </div>
                        </div>
                      }
                    />
                  </div>
                )}
              </div>
              <div style={{
                backgroundColor: 'white',
                border: '1px solid rgb(204, 204, 204)',
              }}>
                <ImpactBubbleHeaderView
                  onTypeChanged={(type: string) => setType(type)}
                  onTooltipClicked={() => setIsImpactDialogOpen(true)}
                />

                {loading ? <LoadingPlaceholder /> : (
                  (application && type && !error) ? (
                    <ImpactBubbleChartView
                      id={application.id}
                      type={type}
                    />
                  ) : (
                    <ImpactChartNoDataPlaceholder error={error}/>
                  ))}
              </div>
              {isImpactDialogOpen && (
                <ImpactScoreExplanationModal
                  score={impactScore}
                  isOpen={isImpactDialogOpen}
                  toggle={() => setIsImpactDialogOpen(false)}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>}
    </Layout>
  );
};

const StatsRow = ({
  label,
  value,
  onClick,
  hover,
}: {
  label: string,
  value?: ReactNode
  onClick?: () => void,
  hover?: ReactNode
}) => {
  const {current: id} = useRef(`id-${uuid().replace(/-/g, '')}`);
  return (
    <div className="StatsRow">
      <div className="StatsRow__label-container" id={id}>
        {onClick ? <button className="StatsRow__label-button" onClick={onClick}><span>{label}</span></button> : <span>{label}</span>}
      </div>
      <div className="StatsRow__value-container">
        {typeof value === 'undefined' ? <LoadingPlaceholder scale={0.75}/> : (
        typeof value === 'string' ? <StringStat value={value}/> : typeof value === 'number' ? <NumericStat value={value} /> : value
      )}
      </div>
      {typeof hover !== 'undefined' && (
        <UncontrolledTooltip target={`#${id}`}>
          {hover}
        </UncontrolledTooltip>
      )}
    </div>
  );
};

const NumericStat = ({value}: {value: number}) => {
  const {current: id} = useRef(`id-${uuid().replace(/-/g, '')}`);
  const shouldAbbreviateValue = value > 10000;
  const displayValue = shouldAbbreviateValue ? formatDashboardNumbers(value) : value;
  return (
    <>
      <span id={id}>
        {displayValue}
      </span>
      {shouldAbbreviateValue && (
        <UncontrolledTooltip target={`#${id}`}>
          {f(value)}
        </UncontrolledTooltip>
      )}
    </>
  );
};

const StringStat = ({value}: {value: string}) => <span>{value}</span>;
